// 🔹 Stylesheets
import '../../index.css';
import '../../route.css';
import '../../reset.css';

// 🔹 React & Hooks
import React, { useRef, useState, useEffect, useCallback, useMemo, FC, ReactNode } from 'react';
import { motion } from 'motion/react';

// 🔹 Routing
import { useLocation, useNavigate } from 'react-router-dom';

// 🔹 UI
import { Slider } from 'antd';

// 🔹 Internal Components
import { Button } from 'src/components/Button/Button.tsx';
import ProgramUserSelect from 'src/components/ProgramUserSelect/ProgramUserSelect.component';
import TrainingNavigationButton from 'src/components/TrainingNavigationButton/TrainingNavigationButton.component';
import { AnimatedStep } from 'src/components/Animation/AnimatedStep';
import { TextAnimate } from 'src/components/Animation/text-animate';
import AutoTooltip from 'src/components/AutoCloseTooltip/AutoCloseTooltip';
import CommandDialog from 'src/components/Modals/CommandDialog/CommandDialog';
import SEO from 'src/components/SEO';

// 🔹 GSAP
import gsap from '../../utils/gsapSetup';

// 🔹 Store
import {
  trainingStore,
  setClient,
  toggleAssessmentDay,
  setSelectedDays,
} from 'src/store/trainingStore';

// 🔹 API Hooks
import {
  useSummarizeInstruction,
  useGenerateWorkoutProgram,
  WorkoutProgramParams,
} from 'src/api/mutations/useWorkoutProgram';

// 🔹 Config
import {
  fitnessLevels,
  trainerProgramUser,
  trainingActivityLike,
  trainingEnergyLevel,
  trainingGoal,
  trainingIntensity,
  trainingMovementGrade,
  trainingScheduleFlexibility,
  traniningLimitation,
} from 'src/config/traniner.config';

// 🔹 Assets
// Using static folder path for reliable access
import MiniPTGIF from 'url:../../assets/videos/mini-pt-care.mp4';
import { useSliderDotClick } from 'src/components/useSliderDotClick/useSliderDotClick';
import { useScreenTime } from 'src/hooks/useScreenTime';
import { STEP_TO_SECTION, screenTimeTracker } from 'src/utils/screenTimeTracker';
import { trackUserBehavior } from 'src/api/mutations/useUserBehaviorTracker';
import { clearGeneratedProgram } from 'src/store/programStore';
import { MuscleGroupImages } from '../OnboardingTrainer/screens/Dashboard/screens/ProgramGeneratorAI/assets/MuscleGroupImages';

// Type
type StepKey =
  | 'title'
  | 'description'
  | 'fit-level'
  | 'goal'
  | 'mobility'
  | 'stability'
  | 'balance'
  | 'movement'
  | 'duration'
  | 'reminder'
  | 'overall';

type StepAnimationState = {
  [step: number]: Record<StepKey, boolean>;
};

// Strings
const titleText =
  'Hi! I’m MiniPT Core and I’ll take care of building your workout program for you.';

const introTime = 1500;
const introButton = 200;

const formatName = (name: string | undefined, isMobile: boolean) => {
  if (!name) return '';

  if (!isMobile) return name;

  const parts = name.split(' ');

  if (parts.length < 2) return name;

  return (
    <>
      {parts[0]} <br />
      {parts.slice(1).join(' ')}
    </>
  );
};

const renderFormattedText = (text: string) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/gs);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const information = (
  <svg
    className="postion-relative-2"
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99967 14.6663C11.6816 14.6663 14.6663 11.6816 14.6663 7.99967C14.6663 4.31778 11.6816 1.33301 7.99967 1.33301C4.31778 1.33301 1.33301 4.31778 1.33301 7.99967C1.33301 11.6816 4.31778 14.6663 7.99967 14.6663Z"
      stroke="white"
      strokeOpacity="0.7"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 10.6663V7.99967M8 5.33301H8.00667"
      stroke="white"
      strokeOpacity="0.7"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LoadingDots = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % 4); // --> 0 to 3 dots
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <span>
      Loading
      <span style={{ display: 'inline-block', width: '24px', textAlign: 'left' }}>
        {/* --> fixed width prevents whole text from moving */}
        {'.'.repeat(index)}
      </span>
    </span>
  );
};

export const Trainer: FC = () => {
  // Use
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const miniPTGifRef = useRef<HTMLDivElement>(null);
  const miniPTGifTextRef = useRef<HTMLSpanElement>(null);
  const miniPTGifScreen = useRef<HTMLDivElement>(null);
  const miniPTGifScreen2 = useRef<HTMLDivElement>(null);
  const activityLikeRef = useRef<HTMLDivElement | null>(null);
  const fitnessLevelRef = useRef<HTMLDivElement | null>(null);

  const mobilityLevelRef = useRef<HTMLDivElement>(null);
  const stabilityControlRef = useRef<HTMLDivElement>(null);
  const balanceConsistencyRef = useRef<HTMLDivElement>(null);
  const movementSymmetryRef = useRef<HTMLDivElement>(null);
  const overallMovementQualityRef = useRef<HTMLDivElement>(null);
  const programDurationRef = useRef<HTMLDivElement>(null);

  // Store
  const assessment = trainingStore((state) => state.assessment || {});

  // Client Firstname
  const firstName = assessment?.selectedClient?.name?.split(' ')[0] || 'Client';

  // Global State
  const [step, setStep] = useState(1);
  const [stepAnimation, setStepAnimation] = useState<StepAnimationState>({});

  // Mutations
  const {
    mutate: requestSummaryInstruction,
    data: summaryData,
    isPending: isSummarizing,
  } = useSummarizeInstruction();
  const { mutate: generateWorkout, isPending: isGenerating } = useGenerateWorkoutProgram();

  // Who is this program for? (Step 3)
  const [selectedClient, setSelectedClient] = useState<string[]>([]);

  // Let’s build the base for Client Firstname’s workout plan. (Step 4)
  const clientIntroTitle = `Let’s build the base for ${firstName}’s workout plan.`;

  // What's Client Firstname's baseline energy level? (Step 5)
  const [selectedEnergyLevel, setSelectedEnergyLevel] = useState<string | null>();

  // What's Client Firstname's non-gym activity like? (Step 6)
  const [activityLike, setActivityLike] = useState<number>(0);
  const [selectedScheduleFlexibility, setSelectedScheduleFlexibility] = useState<string | null>();

  // What’s the intensity vibe for this program? (Step 7)
  const [selectedIntensityVibe, setSelectedIntensityVibe] = useState<string | null>();

  // What’s their fitness level? (Step 8)
  const [fitnessLevel, setFitnessLevel] = useState<number>(3);

  // What is the training goal? (Step 9)
  const [selectedGoal, setSelectedGoal] = useState<string | null>();
  const [selectedGoalStyle, setSelectedGoalStyle] = useState<string | null>();

  // Movement Insights (Step 10)
  const [mobilityLevel, setMobilityLevel] = useState<number>(3);
  const [stabilityControl, setStabilityControl] = useState<number>(2);
  const [balanceConsistency, setBalanceConsistency] = useState<number>(3);
  const [movementSymmetry, setMovementSymmetry] = useState<number>(1);
  const [overallMovementQuality, setOverallMovementQuality] = useState<number>(2);

  // Program Duration (Step 12)
  const [programDuration, setProgramDuration] = useState<number>(0);
  const [minute, setMinute] = useState<number | null>(null);

  // How often will they train? (Step 13)
  const [selectedTime, setSelectedTime] = useState<string[]>([]);

  // What should this program focus on? (Step 14)
  const [selectedFocusProgram, setSelectedFocusProgram] = useState<string[]>([]);

  //Any injuries or limitations to keep in mind? (Step 15)
  const [selectedInjuries, setSelectedInjuries] = useState<string[]>([]);
  const [requirement, setRequirement] = useState<string | ''>('');
  const [selectedDays, setSelectedDaysLocal] = useState<string[]>(['Mon', 'Wed', 'Fri']);
  const [selectedSummaryPrompts, setSelectedSummaryPrompts] = useState<string[]>([]);
  const [generateErrorVisible, setGenerateErrorVisible] = useState(false);

  // Device
  const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const updateDevice = () => {
      const width = window.innerWidth;

      if (width <= 575) {
        setDevice('mobile');
      } else if (width <= 991) {
        setDevice('tablet');
      } else {
        setDevice('desktop');
      }
    };

    updateDevice();
    window.addEventListener('resize', updateDevice);

    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  const buildWorkoutProgramPayload = useCallback(
    (extraInfo: string): WorkoutProgramParams => ({
      user_id: 'user123',
      user_name: firstName,
      body_weight: '180',
      mobility_level: trainingMovementGrade?.[mobilityLevel - 1]?.name || 'Medium',
      stability_level: trainingMovementGrade?.[stabilityControl - 1]?.name || 'Medium',
      balance_consistency: trainingMovementGrade?.[balanceConsistency - 1]?.name || 'Medium',
      movement_symmetry: trainingMovementGrade?.[movementSymmetry - 1]?.name || 'Medium',
      overall_movement_quality:
        trainingMovementGrade?.[overallMovementQuality - 1]?.name || 'Medium',
      program_name: 'Generated Program',
      program_length: programDuration,
      workout_duration: `${minute} minutes`,
      fitness_level: fitnessLevels?.[fitnessLevel - 1]?.name || 'Intermediate',
      energy_level:
        trainingEnergyLevel?.find((e) => e.value === selectedEnergyLevel)?.name || 'Medium',
      daily_physical_load: trainingActivityLike?.[activityLike - 1]?.name || 'Moderate',
      goals: trainingGoal?.find((g) => g.value === selectedGoal)?.name || 'Build muscle',
      training_goal: selectedGoalStyle || 'Strength',
      training_style: selectedGoalStyle || 'Strength',
      focused_area: selectedFocusProgram.join(', '),
      muscles: selectedFocusProgram,
      selected_days: assessment.selectedDays || [],
      selected_time: selectedTime.join(', '),
      workout_intensity:
        trainingIntensity?.find((i) => i.value === selectedIntensityVibe)?.name || 'Moderate',
      injured_body_parts: selectedInjuries.join(', '),
      equiptments: [
        'Dumbbell',
        'Bodyweight',
        'Weight Plates',
        'Benches',
        'Power Racks/Squat Racks',
        'Cable Machines/Functional Trainers',
        'Leg Press Machine',
        'Lat Pulldown/Seated Row Machine',
        'Leg Extension/Leg Curl Machine',
        'Smith Machine',
        'Pec Deck/Chest Fly Machine',
        'Treadmills',
        'Rowing Machines',
        'Exercise Bikes',
        'Stair Climber/Elliptical',
        'Resistance Bands',
        'Pull-up Bars',
        'Yoga/Gym Mats',
      ],
      activation_date: '2026-04-23',
      preferences: requirement.trim().length > 0 ? requirement.trim() : 'None',
      extra_info: extraInfo.trim().length > 0 ? extraInfo.trim() : 'None',
      model: 'gemini-2.5-flash',
    }),
    [
      activityLike,
      assessment.selectedDays,
      balanceConsistency,
      firstName,
      fitnessLevel,
      minute,
      mobilityLevel,
      movementSymmetry,
      overallMovementQuality,
      programDuration,
      requirement,
      selectedEnergyLevel,
      selectedFocusProgram,
      selectedGoal,
      selectedGoalStyle,
      selectedInjuries,
      selectedIntensityVibe,
      selectedTime,
      stabilityControl,
    ],
  );

  const handleSummaryPrompt = useCallback(
    (prompt?: string) => {
      if (containerRef.current) {
        gsap.to(window, {
          duration: 0.6,
          scrollTo: containerRef.current,
          ease: 'power2.inOut',
        });
      }

      setSelectedSummaryPrompts((prev) => {
        const nextPrompts = prompt
          ? prev.includes(prompt)
            ? prev.filter((item) => item !== prompt)
            : [...prev, prompt]
          : [];
        const summaryInstruction = nextPrompts.length > 0 ? nextPrompts.join('. ') : '';

        requestSummaryInstruction(buildWorkoutProgramPayload(summaryInstruction));

        return nextPrompts;
      });
    },
    [buildWorkoutProgramPayload, requestSummaryInstruction],
  );

  // Stable fingerprint of every field that feeds into buildWorkoutProgramPayload.
  // Uses the same dependency list as buildWorkoutProgramPayload so any user change
  // that would alter the payload is automatically detected.
  const summaryPayloadFingerprint = useMemo(
    () =>
      JSON.stringify({
        activityLike,
        balanceConsistency,
        firstName,
        fitnessLevel,
        minute,
        mobilityLevel,
        movementSymmetry,
        overallMovementQuality,
        programDuration,
        requirement,
        selectedEnergyLevel,
        selectedFocusProgram: [...selectedFocusProgram].sort(),
        selectedGoal,
        selectedGoalStyle,
        selectedInjuries: [...selectedInjuries].sort(),
        selectedIntensityVibe,
        selectedTime: [...selectedTime].sort(),
        stabilityControl,
        selectedDays: [...(assessment.selectedDays || [])].sort(),
      }),
    [
      activityLike,
      assessment.selectedDays,
      balanceConsistency,
      firstName,
      fitnessLevel,
      minute,
      mobilityLevel,
      movementSymmetry,
      overallMovementQuality,
      programDuration,
      requirement,
      selectedEnergyLevel,
      selectedFocusProgram,
      selectedGoal,
      selectedGoalStyle,
      selectedInjuries,
      selectedIntensityVibe,
      selectedTime,
      stabilityControl,
    ],
  );

  // Tracks the fingerprint that was used for the most recent summary API call.
  // Stored in a ref so changes to it never trigger a re-render.
  const lastSummaryFingerprintRef = useRef<string | null>(null);

  // Fetch summary when reaching step 16, and re-fetch whenever the payload
  // data has changed since the last call (user went back, edited, returned).
  useEffect(() => {
    if (step !== 16 || isSummarizing) return;

    const isFirstFetch = lastSummaryFingerprintRef.current === null;
    const dataChanged = lastSummaryFingerprintRef.current !== summaryPayloadFingerprint;

    if (isFirstFetch || dataChanged) {
      lastSummaryFingerprintRef.current = summaryPayloadFingerprint;
      // handleSummaryPrompt() with no argument resets selectedSummaryPrompts to []
      // and fires a fresh API call with the current payload.
      handleSummaryPrompt();
    }
  }, [handleSummaryPrompt, isSummarizing, step, summaryPayloadFingerprint]);

  const hasSummaryResponse = Boolean(summaryData?.response);
  const canProceedFromSummary = hasSummaryResponse && !isSummarizing;

  // Init Screen
  useEffect(() => {
    // Initial GSAP setup for State 1 (Rising Sun)
    if (miniPTGifRef.current) {
      gsap.set(miniPTGifRef.current, {
        xPercent: -50,
        yPercent: -50,
        y: '70vh', // Positioned at the bottom (acting like a rising sun)
        scale: 4, // Large size
        opacity: 0.8,
      });
      if (window.innerWidth < 576) {
        gsap.set(miniPTGifRef.current, {
          width: 150,
          height: 150,
          top: `40%`,
        });
      }
    }

    if (miniPTGifTextRef.current) {
      gsap.set(miniPTGifTextRef.current, { opacity: 0 }); // hide the "MiniPT Core" text
    }

    if (miniPTGifScreen2.current) {
      gsap.set(miniPTGifScreen2.current, { opacity: 0, display: 'none' });
    }
  }, []);

  // Handler
  // Init Screen Handler
  const handleCreateProgram = useCallback(
    (value: number) => {
      // Prevent double execution
      if (step === 2) return;

      const tl = gsap.timeline({
        onComplete: () => {
          const nextStep = typeof value === 'number' && Number.isFinite(value) ? value : 2;
          // eslint-disable-next-line react-hooks/immutability
          resetStep(nextStep);
          setStep(nextStep);
          localStorage.removeItem('minipt-tour-done');
          if (nextStep === 2) {
            setSelectedDays(selectedDays);
            setSelectedDaysLocal([]);
            // Wipe any previously generated program so a fresh run starts clean.
            clearGeneratedProgram();
          }
        },
      });

      // 1. Fade out Screen 1 smoothly
      tl.to(
        miniPTGifScreen.current,
        {
          y: -30,
          opacity: 0,
          duration: 0.5,
          ease: 'power2.inOut',
          onComplete: () => {
            gsap.set(miniPTGifScreen.current, { display: 'none' });
            const parent = miniPTGifScreen.current?.parentElement;
            parent?.classList.remove('flex-center-column');
            gsap.set(miniPTGifRef.current, { top: 'calc(var(--padding-top) + 250px + 32px)' });
          },
        },
        0,
      );

      // 2. Interpolate Circle scale, Y-axis Translation, and explicit dimensions simultaneously
      // Goal: "arrive at the top and hit its final size simultaneously"
      tl.to(
        miniPTGifRef.current,
        {
          yPercent: -100,
          y: 'calc(var(--padding-top) - 125px)',
          scale: 1, // Final normal size
          width: 250, // Smoothly animate to 300px
          height: 250, // Smoothly animate to 300px
          opacity: 1,
          duration: 1.5,
          ease: 'power3.inOut', // Smooth power ease matching the 'sliding' smoothness reference
        },
        0.2,
      ); // Start slightly after screen 1 starts fading out

      // Fade in the text inside circle after the scale down
      tl.to(
        miniPTGifTextRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        1.2,
      );

      // 3. Fade in Screen 2
      tl.set(miniPTGifScreen2.current, { display: 'flex' }, 1.0); // Display slightly before fading to avoid flicker
      tl.fromTo(
        miniPTGifScreen2.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
        },
        1.1, // Fade in relative to circle animation arriving
      );
    },
    [selectedDays, step],
  );

  useEffect(() => {
    if (location.search) {
      const path = location.search.split('?')?.[1] || null;
      if (path) {
        const step = {
          'program-let-start-intro': 2,
          'program-for': 3,
          'program-client-select-intro': 4,
          'program-energy-level': 5,
          'program-activity': 6,
          'program-intensity': 7,
          'program-level': 8,
          'program-goal': 9,
          'program-movement-insight': 10,
          'program-movement-insight-intro': 11,
          'program-run': 12,
          'program-frequency': 13,
          'program-focus': 14,
          'program-injuries': 15,
          'program-generate': 16,
        }[path];
        if (step) handleCreateProgram(step);
      }
    }
  }, [handleCreateProgram, location.search]);

  // Animation For Global
  const updateStepAnimation = (step: number, key: StepKey, value: boolean = true) => {
    setStepAnimation((prev) => ({
      ...prev,
      [step]: {
        ...prev[step],
        [key]: value,
      },
    }));
  };

  // Reset Animation For Global
  const resetStep = (step: number) => {
    setStepAnimation((prev) => {
      const currentStep = prev[step];

      if (!currentStep) return prev; // 👈 prevent crash

      return {
        ...prev,
        [step]: Object.keys(currentStep).reduce(
          (acc, key) => {
            acc[key as StepKey] = false;
            return acc;
          },
          {} as Record<StepKey, boolean>,
        ),
      };
    });
  };

  useEffect(() => {
    if (step >= 3 && containerRef.current) {
      gsap.to(window, {
        duration: 0.6,
        scrollTo: containerRef.current,
        ease: 'power2.inOut',
      });
    }
  }, [step]);

  // Who is this program for? (Step 3)
  const toggleClient = useCallback((id: string) => {
    setSelectedClient([id]);

    const selectedUser = trainerProgramUser.find((user) => user._id === id);
    if (!selectedUser) return;

    setClient({
      _id: selectedUser._id,
      name: selectedUser.name,
      avatar: selectedUser.avatar,
    });

    setTimeout(() => {
      setStep(4);
    }, introButton);
  }, []);

  // What's Client Firstname's non-gym activity like? (Step 6)
  const handleActivityLike = (val: number) => {
    setActivityLike(val);
    updateStepAnimation(step, 'description');
  };

  // What’s their fitness level? (Step 8)
  const handleFitnessLevel = (val: number) => {
    setFitnessLevel(val);
    updateStepAnimation(step, 'fit-level');
  };

  // What is the training goal? (Step 9)
  const goalHandler = (goal: string) => {
    setSelectedGoal(goal);
    setSelectedGoalStyle(null);
    updateStepAnimation(step, 'goal');
  };

  const goalStyleHandler = (goal: string) => {
    setSelectedGoalStyle(goal);
    if (goal) {
      setTimeout(() => {
        setStep(10);
      }, introButton);
    }
  };

  // Movement Insights (Step 10)
  const handleMobilityLevelChange = (val: number) => {
    setMobilityLevel(val);
  };

  const handleStabilityControlChange = (val: number) => {
    setStabilityControl(val);
  };

  const handleBalanceConsistencyChange = (val: number) => {
    setBalanceConsistency(val);
  };

  const handleMovementSymmetryChange = (val: number) => {
    setMovementSymmetry(val);
  };

  const handleOverallMovementQualityChange = (val: number) => {
    setOverallMovementQuality(val);
  };

  // Program Duration (Step 12)
  const handleProgramDuration = (val: number) => {
    setProgramDuration(val);
    updateStepAnimation(step, 'duration');
  };

  // How often will they train? (Step 13)
  const handleToggleDay = (day: string) => {
    toggleAssessmentDay(day);
    if (day) updateStepAnimation(step, 'reminder');
  };

  const toggleTime = (time: string) => {
    setSelectedTime((prev) => (prev.includes(time) ? prev.filter((d) => d !== time) : [time]));
    setTimeout(() => {
      setStep(14);
    }, introButton);
  };

  // What should this program focus on? (Step 14)
  const toggleFocusProgram = (focusProgram: string) => {
    setSelectedFocusProgram((prev) => {
      // If user selects "Full Body", make it exclusive
      if (focusProgram === 'Full Body') {
        return ['Full Body'];
      }

      // If selecting any other program
      // 1. remove "Full Body" if it's currently selected
      const withoutFullBody = prev.filter((p) => p !== 'Full Body');

      // 2. toggle the selected program
      return withoutFullBody.includes(focusProgram)
        ? withoutFullBody.filter((p) => p !== focusProgram)
        : [...withoutFullBody, focusProgram];
    });
  };

  //Any injuries or limitations to keep in mind? (Step 15)
  const injuriesHandler = (injuries: string) => {
    setSelectedInjuries((prev) =>
      prev.includes(injuries) ? prev.filter((d) => d !== injuries) : [...prev, injuries],
    );
  };

  const requirementHandler = (value: string) => {
    setRequirement(value || '');
  };

  // Antd Generate Slide
  // What's Client Firstname's non-gym activity like? (Step 6)
  // 🔥 Generate marks with fitness levels from config
  const activityLikeNames = trainingActivityLike?.map((level) => level.name);
  const activityLikeMarks: Record<number, { label: ReactNode }> = {};
  activityLikeNames.forEach((level, index) => {
    const step = index + 1;
    activityLikeMarks[step] = {
      label: (
        <span
          className={`mark-label ${activityLike === step ? `active-mark` : ''}`}
          onClick={() => handleActivityLike(step)}
        >
          {level}
        </span>
      ),
    };
  });

  // 🔥 Generate marks with fitness levels from config
  const fitnessLevelNames = fitnessLevels?.map((level) => level.name);
  const fitnessLevelMarks: Record<number, { label: ReactNode }> = {};
  fitnessLevelNames.forEach((level, index) => {
    const step = index + 1;
    fitnessLevelMarks[step] = {
      label: (
        <span
          className={`mark-label ${fitnessLevel === step ? `active-mark` : ''}`}
          onClick={() => handleFitnessLevel(step)}
        >
          {level}
        </span>
      ),
    };
  });

  // 🔥 Generate marks with fitness levels from config
  const trainingMovementNames = trainingMovementGrade?.map((level) => level.name);
  const mobilityLevelMarks: Record<number, { label: ReactNode }> = {};
  trainingMovementNames.forEach((level, index) => {
    const step = index + 1;
    mobilityLevelMarks[step] = {
      label: (
        <span
          className={`mark-label ${mobilityLevel === step ? `active-mark` : ''}`}
          onClick={() => handleMobilityLevelChange(step)}
        >
          {level}
        </span>
      ),
    };
  });

  const stabilityControlMarks: Record<number, { label: ReactNode }> = {};
  trainingMovementNames.forEach((level, index) => {
    const step = index + 1;
    stabilityControlMarks[step] = {
      label: (
        <span
          className={`mark-label ${stabilityControl === step ? `active-mark` : ''}`}
          onClick={() => handleStabilityControlChange(step)}
        >
          {level}
        </span>
      ),
    };
  });

  const balanceConsistencyMarks: Record<number, { label: ReactNode }> = {};
  trainingMovementNames.forEach((level, index) => {
    const step = index + 1;
    balanceConsistencyMarks[step] = {
      label: (
        <span
          className={`mark-label ${balanceConsistency === step ? `active-mark` : ''}`}
          onClick={() => handleBalanceConsistencyChange(step)}
        >
          {level}
        </span>
      ),
    };
  });

  const movementSymmetryMarks: Record<number, { label: ReactNode }> = {};
  trainingMovementNames.forEach((level, index) => {
    const step = index + 1;
    movementSymmetryMarks[step] = {
      label: (
        <span
          className={`mark-label ${movementSymmetry === step ? `active-mark` : ''}`}
          onClick={() => handleMovementSymmetryChange(step)}
        >
          {level}
        </span>
      ),
    };
  });

  const overallMovementQualityMarks: Record<number, { label: ReactNode }> = {};
  trainingMovementNames.forEach((level, index) => {
    const step = index + 1;
    overallMovementQualityMarks[step] = {
      label: (
        <span
          className={`mark-label ${overallMovementQuality === step ? `active-mark` : ''}`}
          onClick={() => handleOverallMovementQualityChange(step)}
        >
          {level}
        </span>
      ),
    };
  });

  // 🔥 Generate marks (1 → 12)
  const programDurationMarks: Record<number, { label: ReactNode }> = Array.from({
    length: 12,
  }).reduce(
    (acc: Record<number, { label: ReactNode }>, _, i) => {
      const step = i + 1;

      acc[step] = {
        label: (
          <span
            className={`mark-label ${programDuration === step ? `active-mark` : ''}`}
            onClick={() => handleProgramDuration(step)}
          >
            {step}
          </span>
        ),
      };

      return acc;
    },
    {} as Record<number, { label: ReactNode }>,
  );

  const isSelected = (day: string) => assessment.selectedDays?.includes(day) || false;
  const isSelectedTime = (time: string) => selectedTime.includes(time);
  const isSelectedFocusProgram = (focusProgram: string) =>
    selectedFocusProgram.includes(focusProgram);
  const isSelectedInjuries = (injuries: string) => selectedInjuries.includes(injuries);
  const getSelectedGoal = trainingGoal?.find((goal) => goal.value === selectedGoal);

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const times = ['Morning', 'Lunch', 'Evening '];
  const focusProgram = ['Full Body', 'Upper Body', 'Lower Body', 'Core', 'Back', 'Glutes'];
  const prompts = ['Gradually raise workout intensity', 'All weeks should have the same workout'];

  const stepClass =
    {
      1: 'page-program-creator-demo',
      2: 'page-program-creator-demo-intro',
      3: 'page-program-for',
      5: 'page-program-energy-level',
      6: 'page-program-activity',
      7: 'page-program-intensity',
      8: 'page-program-level',
      9: 'page-program-goal',
      10: 'page-program-movement-insight',
      12: 'page-program-run',
      13: 'page-program-frequency',
      14: 'page-trainer-program-focus',
      15: `page-trainer-program-injuries`,
      16: 'page-trainer-program-generate',
    }[step] || '';

  const nextDisableStep = step
    ? {
        5: !selectedEnergyLevel,
        6: !selectedScheduleFlexibility && activityLike >= 0,
        7: !selectedIntensityVibe,
        8: !(fitnessLevel > 0),
        9: !(selectedGoal && selectedGoalStyle),
        10: !(
          mobilityLevel > 0 &&
          stabilityControl > 0 &&
          balanceConsistency > 0 &&
          movementSymmetry > 0 &&
          overallMovementQuality > 0
        ),
        12: !(programDuration && minute),
        13: !(assessment.selectedDays?.length && selectedTime?.length),
        14: !selectedFocusProgram?.length,
        16: true,
      }[step] || false
    : true;

  useSliderDotClick({
    ref: activityLikeRef,
    step,
    targetStep: 6,
    onDotClick: () => handleActivityLike(1),
  });

  useSliderDotClick({
    ref: fitnessLevelRef,
    step,
    targetStep: 8,
    onDotClick: () => handleFitnessLevel(1),
  });

  useSliderDotClick({
    ref: mobilityLevelRef,
    step,
    targetStep: 10,
    onDotClick: () => handleMobilityLevelChange(1),
  });

  useSliderDotClick({
    ref: stabilityControlRef,
    step,
    targetStep: 10,
    onDotClick: () => handleStabilityControlChange(1),
  });

  useSliderDotClick({
    ref: balanceConsistencyRef,
    step,
    targetStep: 10,
    onDotClick: () => handleBalanceConsistencyChange(1),
  });

  useSliderDotClick({
    ref: movementSymmetryRef,
    step,
    targetStep: 10,
    onDotClick: () => handleMovementSymmetryChange(1),
  });

  useSliderDotClick({
    ref: overallMovementQualityRef,
    step,
    targetStep: 10,
    onDotClick: () => handleOverallMovementQualityChange(1),
  });

  useSliderDotClick({
    ref: programDurationRef,
    step,
    targetStep: 12,
    onDotClick: () => handleProgramDuration(1),
  });

  // ── Screen time tracking ──────────────────────────────────────────────────
  // Automatically tracks time per section; skips automated transitions (steps 2, 4, 11).
  // Pass active=false when the step has no mapping so an empty-string section
  // is never created in the tracker (guards against unknown step values).
  const _currentSection = STEP_TO_SECTION[step] ?? '';
  useScreenTime(_currentSection, Boolean(_currentSection));

  return (
    <>
      <SEO
        title="miniPT for Trainers"
        description="Manage your clients, create personalized programs, and grow your fitness business with miniPT."
        canonicalUrl="/trainer"
      />
      <div
        className={`miniPT-container miniPT-training-container ${(step > 2 && step <= 10) || (step > 12 && step <= 16) ? `miniPT-animation` : ``} ${stepClass}`}
        ref={containerRef}
        data-step={step}
      >
        <CommandDialog
          visible={generateErrorVisible}
          onClose={() => setGenerateErrorVisible(false)}
          type="error"
          title="We couldn’t generate the program"
          description="Something went wrong while creating the workout program. Please try again later."
          confirmText="Okay"
        />

        {/* Absolute Flow: The Animated AI Circle */}
        <div
          className={`flex-center-column miniPT-animation-container ${isGenerating || isSummarizing ? `ai-program-generating` : ``}`}
          ref={miniPTGifRef}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            width: 300,
            height: 300,
            zIndex: 0,
            pointerEvents: 'none',
            mixBlendMode: 'lighten',
          }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              mixBlendMode: 'lighten',
            }}
          >
            <source src={MiniPTGIF} type="video/mp4" />
          </video>
          <span ref={miniPTGifTextRef} className="mini-pt-text">
            MiniPT Core
          </span>
        </div>

        <div
          className={`background-wrapper flex-center-column px-16`}
          style={{ position: 'relative', zIndex: 1, width: '100%' }}
        >
          {/* Screen 1 Content */}
          <div
            ref={miniPTGifScreen}
            className="background-wrapper-content flex-center-column"
            style={{ marginTop: '-10vh' }}
          >
            <div className={`creator-card`} style={{ textTransform: 'uppercase' }}>
              Program creator DEMO
            </div>
            <h1 className={`page-title`} style={{ textAlign: 'center' }}>
              Build structured workout <br />
              programs <span className={`text-primary-light`}>10x faster</span>
            </h1>
            <p style={{ textAlign: 'center' }} className="mt-0 text-light fs-16">
              Define your structure once, then let AI generate a complete program instantly.
            </p>
            <p style={{ textAlign: 'center' }} className="sound-great fs-16">
              Sounds great! Let’s start
            </p>
            <Button
              onClick={handleCreateProgram}
              variant="primary"
              className={`bg-gradient-color relative-z-10`}
            >
              Create a Program
            </Button>
          </div>

          {/* Screen 2 Content */}
          <div
            ref={miniPTGifScreen2}
            className="flex-center-column animation-margin-top"
            // style={textWrapperMarginTop ? { marginTop: textWrapperMarginTop } : {}}
          >
            {step === 2 && (
              <div className="max-width-768">
                <TextAnimate
                  animation="slideUp"
                  by="word"
                  as="h2"
                  center={true}
                  className="page-intro-title mt-0"
                  duration={1}
                  delay={0.05}
                  onComplete={() => {
                    setTimeout(() => {
                      setStep(3);
                    }, introTime);
                  }}
                >
                  {titleText}
                </TextAnimate>
              </div>
            )}

            {step === 3 && (
              <div className="max-width-768 route-fade-in">
                <h1 className="page-sub-title text-center mt-0 page-sub-title-margin">
                  Who is this program for?
                </h1>
                <p className="text-light text-center mt-0 page-secondary-margin fs-18">
                  Let’s select a client from your list.
                </p>
                <ProgramUserSelect
                  users={trainerProgramUser}
                  selectedIds={selectedClient}
                  toggleUser={toggleClient}
                />
              </div>
            )}

            {step === 4 && (
              <div className="max-width-768">
                <TextAnimate
                  animation="slideUp"
                  by="word"
                  as="h2"
                  center={true}
                  className="page-intro-title mt-0"
                  duration={1}
                  delay={0.05}
                  onComplete={() => {
                    setTimeout(() => {
                      setStep(5);
                    }, introTime);
                  }}
                >
                  {clientIntroTitle}
                </TextAnimate>
              </div>
            )}

            {step === 5 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`What's ${firstName}'s baseline energy level?`}</h1>
                  <p className="text-light text-center mt-0 mb-32 fs-18">
                    I’ll use this to set the baseline for their recovery and intensity curves.
                  </p>

                  <div className="flex-button max-width-576">
                    {trainingEnergyLevel?.map((energy) => (
                      <Button
                        onClick={() => {
                          setSelectedEnergyLevel(energy?.value);
                          setTimeout(() => {
                            setStep(6);
                          }, introButton);
                        }}
                        key={energy?.value}
                        variant="primary"
                        className={`${selectedEnergyLevel && selectedEnergyLevel === energy?.value ? `active` : ``} button-svg flex-md-1`}
                        svg={energy?.icon}
                      >
                        <span className="flex-1">{energy?.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 6 && (
              <div className="max-width-768 route-fade-in">
                <h1 className="page-sub-title text-center mt-0 mb-12">{`What's ${firstName || ''}'s non-gym activity like?`}</h1>
                <p className="text-light text-center mt-0 mb-32 fs-18">
                  {`We account for their daily physical load to ensure the program isn't overreaching.`}
                </p>

                <div className="max-width-768 gradient-wrapper" ref={activityLikeRef}>
                  <Slider
                    min={1}
                    max={3}
                    step={activityLike}
                    value={activityLike ?? undefined}
                    onChange={handleActivityLike}
                    marks={activityLikeMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${activityLike ? `` : `not-select`}`}
                  />

                  <AnimatedStep isVisible={activityLike > 0}>
                    <div
                      className="gradient-description-container"
                      style={activityLike ? { marginTop: 40 } : {}}
                    >
                      <ul className="gradient-description-list" key={activityLike}>
                        {trainingActivityLike[activityLike - 1]?.descriptions?.map(
                          (description, index) => (
                            <motion.li
                              key={index}
                              initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)' }}
                              animate={{ opacity: 0.7, clipPath: 'inset(0 0 0 0)' }}
                              transition={{
                                delay: index * 0.1,
                                duration: 0.8,
                                ease: 'easeInOut',
                              }}
                              className="gradient-description-item text-light slide-up-item"
                            >
                              {description}
                            </motion.li>
                          ),
                        )}
                      </ul>
                    </div>
                  </AnimatedStep>
                </div>

                <AnimatedStep
                  isVisible={activityLike > 0 && stepAnimation?.[step]?.description}
                  className="text-center"
                >
                  <p
                    style={{ marginTop: 21, marginBottom: 16 }}
                    className="fs-18 client-best-style"
                  >
                    How flexible is {firstName}&#39;s schedule?
                  </p>

                  <div className="flex-button">
                    {trainingScheduleFlexibility?.map((schedule) => (
                      <Button
                        onClick={() => {
                          setSelectedScheduleFlexibility(schedule?.value);
                          if (schedule?.value) {
                            setTimeout(() => {
                              setStep(7);
                            }, introButton);
                          }
                        }}
                        key={schedule?.value}
                        variant="primary"
                        className={`${selectedScheduleFlexibility && selectedScheduleFlexibility === schedule?.value ? `active` : ``} flex-md-120`}
                      >
                        {schedule?.name}
                      </Button>
                    ))}
                  </div>
                </AnimatedStep>
              </div>
            )}

            {step === 7 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`What’s the intensity vibe for this program?`}</h1>
                  <p className="text-light text-center mt-0 mb-32">
                    {`Pick the baseline stimulus you want Oliver to experience.`}
                  </p>
                  <div className="flex-button">
                    {trainingIntensity?.map((energy) => (
                      <Button
                        onClick={() => {
                          setSelectedIntensityVibe(energy?.value);
                          if (energy?.value) {
                            setTimeout(() => {
                              setStep(8);
                            }, introButton);
                          }
                        }}
                        key={energy?.value}
                        variant="primary"
                        className={`${selectedIntensityVibe && selectedIntensityVibe === energy?.value ? `active` : ``} button-svg flex-md-160`}
                        svg={energy?.icon}
                      >
                        <span className="flex-1">
                          {formatName(energy?.name, device === 'mobile')}
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 8 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`What’s their fitness level?`}</h1>
                  <p className="text-light text-center mt-0 mb-32">
                    Looks like your client is{' '}
                    {fitnessLevel > 0 ? fitnessLevelNames[fitnessLevel - 1] : 'Intermediate'} level.
                    Do you want to make any changes?
                  </p>
                </div>

                <div className="max-width-768 gradient-wrapper route-fade-in" ref={fitnessLevelRef}>
                  <Slider
                    min={1}
                    max={5}
                    step={fitnessLevel}
                    value={fitnessLevel}
                    onChange={handleFitnessLevel}
                    marks={fitnessLevelMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${fitnessLevel ? `` : `not-select`}`}
                  />

                  {fitnessLevel > 0 && (stepAnimation?.[step]?.['fit-level'] ?? fitnessLevel) && (
                    <>
                      <AnimatedStep
                        isVisible={stepAnimation?.[step]?.['fit-level'] ?? fitnessLevel}
                      >
                        <div className="gradient-description-container" style={{ marginTop: 40 }}>
                          <ul className="gradient-description-list" key={fitnessLevel}>
                            {fitnessLevels[fitnessLevel - 1]?.descriptions?.map(
                              (description, index) => (
                                <motion.li
                                  key={index}
                                  initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)' }}
                                  animate={{ opacity: 0.7, clipPath: 'inset(0 0 0 0)' }}
                                  transition={{
                                    delay: index * 0.1,
                                    duration: 0.8,
                                    ease: 'easeInOut',
                                  }}
                                  className="gradient-description-item text-light slide-up-item"
                                >
                                  {description}
                                </motion.li>
                              ),
                            )}
                          </ul>
                        </div>
                      </AnimatedStep>
                    </>
                  )}
                </div>
              </>
            )}

            {step === 9 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`What is the training goal?`}</h1>
                  <p className="text-light text-center mt-0 mb-32">
                    Looks like your client wants to build muscle. Do you want to make any changes?
                  </p>

                  <div className={`max-width-576 grid-button`}>
                    {trainingGoal.map((goal, index) => (
                      <Button
                        key={goal?.value + index}
                        onClick={() => goalHandler(goal?.value)}
                        variant="primary"
                        className={`${selectedGoal === goal?.value ? `active` : ``}`}
                        svg={goal?.icon}
                      >
                        {goal?.name}
                      </Button>
                    ))}
                  </div>

                  {selectedGoal && getSelectedGoal && (
                    <>
                      <AnimatedStep
                        isVisible={stepAnimation?.[step]?.['goal']}
                        className="text-center"
                      >
                        <p
                          style={{ marginBottom: 16, marginTop: 32 }}
                          className="client-best-style"
                        >{`What training style suits your client best?`}</p>
                        <div className={`max-width-576 grid-button`}>
                          {getSelectedGoal &&
                            getSelectedGoal?.styles?.map((style, index) => (
                              <Button
                                key={style?.value + index}
                                onClick={() => goalStyleHandler(style?.value)}
                                variant="primary"
                                className={`${selectedGoalStyle === style?.value ? `active` : ``} `}
                                svg={style?.icon}
                              >
                                {style?.name}
                              </Button>
                            ))}
                        </div>
                      </AnimatedStep>
                    </>
                  )}
                </div>
              </>
            )}

            {step === 10 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`Movement Insights`}</h1>
                  <p className="text-light text-center mb-32 mt-0">
                    We’ve analysed your client’s movement patterns. You can adjust these if needed.
                  </p>
                </div>
                <div
                  className="max-width-768 gradient-wrapper route-fade-in"
                  ref={mobilityLevelRef}
                >
                  <p className="text-start mt-0 information-primary-text">
                    Mobility level{' '}
                    <AutoTooltip
                      title={`I will use this to set the safe range of motion for your client’s exercises.`}
                    >
                      <span className="information">{information}</span>
                    </AutoTooltip>
                  </p>
                  <p className={`text-light text-start mt-0 mb-2-5 information-text`}>
                    {`I will use this to set the safe range of motion for your client’s exercises.`}
                  </p>
                  <Slider
                    min={1}
                    max={3}
                    step={mobilityLevel}
                    value={mobilityLevel}
                    onChange={handleMobilityLevelChange}
                    marks={mobilityLevelMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${mobilityLevel ? `` : `not-select`}`}
                  />
                </div>

                <div
                  className="max-width-768 gradient-wrapper route-fade-in"
                  ref={stabilityControlRef}
                >
                  <p className="text-start mt-0 information-primary-text">
                    Stability Control{' '}
                    <AutoTooltip
                      title={`This helps me decide when to use supported machines versus free weights.`}
                    >
                      <span className="information">{information}</span>
                    </AutoTooltip>
                  </p>
                  <p className={`text-light text-start mt-0 mb-2-5 information-text`}>
                    This helps me decide when to use supported machines versus free weights.
                  </p>
                  <Slider
                    min={1}
                    max={3}
                    step={stabilityControl}
                    value={stabilityControl}
                    onChange={handleStabilityControlChange}
                    marks={stabilityControlMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${stabilityControl ? `` : `not-select`}`}
                  />
                </div>

                <div
                  className="max-width-768 gradient-wrapper route-fade-in"
                  ref={balanceConsistencyRef}
                >
                  <p className="text-start mt-0 information-primary-text">
                    Balance Consistency{' '}
                    <AutoTooltip
                      title={`This helps to balance the program between unilateral and bilateral movements.`}
                    >
                      <span className="information">{information}</span>
                    </AutoTooltip>
                  </p>
                  <p className={`text-light text-start mt-0 mb-2-5 information-text`}>
                    This helps to balance the program between unilateral and bilateral movements.
                  </p>
                  <Slider
                    min={1}
                    max={3}
                    step={balanceConsistency}
                    value={balanceConsistency}
                    onChange={handleBalanceConsistencyChange}
                    marks={balanceConsistencyMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${balanceConsistency ? `` : `not-select`}`}
                  />
                </div>

                <div
                  className="max-width-768 gradient-wrapper route-fade-in"
                  ref={movementSymmetryRef}
                >
                  <p className="text-start mt-0 information-primary-text">
                    Movement Symmetry{' '}
                    <AutoTooltip
                      title={`This flags where we might need extra corrective work to fix side-to-side
                  imbalances.`}
                    >
                      <span className="information">{information}</span>
                    </AutoTooltip>
                  </p>
                  <p className={`text-light text-start mt-0 mb-2-5 information-text`}>
                    This flags where we might need extra corrective work to fix side-to-side
                    imbalances.
                  </p>
                  <Slider
                    min={1}
                    max={3}
                    step={movementSymmetry}
                    value={movementSymmetry}
                    onChange={handleMovementSymmetryChange}
                    marks={movementSymmetryMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${movementSymmetry ? `` : `not-select`}`}
                  />
                </div>

                <div
                  className="max-width-768 gradient-wrapper route-fade-in"
                  ref={overallMovementQualityRef}
                >
                  <p className="text-start mt-0 information-primary-text">
                    Overall Movement Quality{' '}
                    <AutoTooltip
                      title={`This is my primary filter for determining the total technical complexity of the
                  plan.`}
                    >
                      <span className="information">{information}</span>
                    </AutoTooltip>
                  </p>
                  <p className={`text-light text-start mt-0 mb-2-5 information-text`}>
                    This is my primary filter for determining the total technical complexity of the
                    plan.
                  </p>
                  <Slider
                    min={1}
                    max={3}
                    step={overallMovementQuality}
                    value={overallMovementQuality}
                    onChange={handleOverallMovementQualityChange}
                    marks={overallMovementQualityMarks}
                    tooltip={{ open: false }}
                    className={`mobile-gradient-slider gradient-slider text-start-end ${overallMovementQuality ? `` : `not-select`}`}
                  />
                </div>

                <div className="max-width-next route-fade-in">
                  <Button
                    onClick={() => {
                      setTimeout(() => {
                        setStep(11);
                      }, introButton);
                    }}
                    variant="primary"
                    className={`flex-next`}
                    disabled={
                      !(
                        mobilityLevel > 0 &&
                        stabilityControl > 0 &&
                        balanceConsistency > 0 &&
                        movementSymmetry > 0 &&
                        overallMovementQuality > 0
                      )
                    }
                  >
                    Next
                  </Button>
                </div>
              </>
            )}

            {step === 11 && (
              <div className="max-width-768 route-fade-in">
                <TextAnimate
                  animation="slideUp"
                  by="word"
                  as="h2"
                  center={true}
                  className="page-intro-title mt-0"
                  duration={1}
                  delay={0.05}
                  onComplete={() => {
                    setTimeout(() => {
                      setStep(12);
                    }, introTime);
                  }}
                >
                  {`Great! Now let’s talk about the program`}
                </TextAnimate>
              </div>
            )}

            {step === 12 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`Let’s set the program duration.`}</h1>
                  <p className="text-light text-center mt-0 mb-32">{`How many weeks are we planning for?`}</p>
                  <div className={`max-width-768 gradient-wrapper`} ref={programDurationRef}>
                    <Slider
                      min={1}
                      max={12}
                      step={programDuration}
                      value={programDuration}
                      onChange={handleProgramDuration}
                      marks={programDurationMarks}
                      tooltip={{ open: false }}
                      className={`gradient-slider ${programDuration ? `` : `not-select`}`}
                    />
                  </div>

                  <div className="max-width-768 text-center">
                    <AnimatedStep isVisible={stepAnimation?.[step]?.duration}>
                      <p style={{ marginTop: 21, marginBottom: 16 }} className="client-best-style">
                        How long should be the workouts?
                      </p>
                      <div className={`flex-button`}>
                        <Button
                          onClick={() => {
                            setMinute(15);
                            setTimeout(() => {
                              setStep(13);
                            }, introButton);
                          }}
                          variant="primary"
                          className={`${minute === 15 ? `active` : ``} flex-md-110`}
                        >
                          15 min
                        </Button>
                        <Button
                          onClick={() => {
                            setMinute(30);
                            setTimeout(() => {
                              setStep(13);
                            }, introButton);
                          }}
                          variant="primary"
                          className={`${minute === 30 ? `active` : ``} flex-md-110`}
                        >
                          30 min
                        </Button>
                        <Button
                          onClick={() => {
                            setMinute(45);
                            setTimeout(() => {
                              setStep(13);
                            }, introButton);
                          }}
                          variant="primary"
                          className={`${minute === 45 ? `active` : ``} flex-md-110`}
                        >
                          45 min
                        </Button>
                      </div>
                    </AnimatedStep>
                  </div>
                </div>
              </>
            )}

            {step === 13 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`How often will they train?`}</h1>
                  <div className="max-width-576 ">
                    <p className="text-light text-center mt-0 mb-32">{`Looks like your client set their preferred workout days. Do you want to make
                      any changes?`}</p>
                  </div>
                  <div className={`flex-button flex-days`}>
                    {days.map((day) => (
                      <Button
                        key={day}
                        onClick={() => handleToggleDay(day)}
                        variant="primary"
                        className={`${isSelected(day) ? `active` : ``} flex-md-80 justify-content-center`}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                  <AnimatedStep
                    isVisible={
                      stepAnimation?.[step]?.reminder || assessment.selectedDays?.length > 0
                    }
                  >
                    <div className={`reminder-container`}>
                      <p
                        className={`text-center client-best-style`}
                        style={{ marginBottom: 16, marginTop: 31 }}
                      >{`Let's set a reminder for them`}</p>
                      <div className={`flex-button`}>
                        {times.map((time) => (
                          <Button
                            key={time}
                            onClick={() => toggleTime(time)}
                            variant="primary"
                            className={`${isSelectedTime(time) ? `active` : ``} flex-md-120`}
                          >
                            {time}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </AnimatedStep>
                  <br />
                  <div className="max-width-next route-fade-in">
                    <Button
                      onClick={() => {
                        setTimeout(() => {
                          setStep(13);
                        }, introButton);
                      }}
                      variant="primary"
                      className={`flex-next`}
                      disabled={!selectedTime?.length}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}

            {step === 14 && (
              <>
                <div className="max-width-768 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`What should this program focus on?`}</h1>
                  <p className="text-light text-center mt-0 mb-32">
                    {' '}
                    {`Where are we prioritizing growth?`}
                  </p>
                </div>
                <div
                  className={`program-focus-container trainer-program-focus-wrapper max-width-576 route-fade-in`}
                >
                  <div className={`trainer-program-focus-button-wrapper`}>
                    <div className={`trainer-program-focus-flex-button`}>
                      {focusProgram.map((program) => (
                        <Button
                          key={program}
                          onClick={() => toggleFocusProgram(program)}
                          variant="primary"
                          className={`${isSelectedFocusProgram(program) ? `active` : ``} program-focus-program-button justify-content-center`}
                        >
                          {program}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="trainer-program-focus-image-wrapper">
                    <img
                      src={MuscleGroupImages?.male?.base}
                      className="muscle-group"
                      alt={'muscleGroup'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.full_body}
                      className={`${isSelectedFocusProgram('Full Body') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupFullBody'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.upper_body}
                      className={`${isSelectedFocusProgram('Upper Body') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupUpperBody'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.lower_body}
                      className={`${isSelectedFocusProgram('Lower Body') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupLowerBody'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.core}
                      className={`${isSelectedFocusProgram('Core') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupCore'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.back}
                      className={`${isSelectedFocusProgram('Back') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupBack'}
                    />
                    <img
                      src={MuscleGroupImages?.male?.glutes}
                      className={`${isSelectedFocusProgram('Glutes') ? `active` : ``} muscle-group-item`}
                      alt={'muscleGroupGlutes'}
                    />
                  </div>
                </div>
                <br />
                <div className="max-width-next route-fade-in">
                  <Button
                    onClick={() => {
                      setTimeout(() => {
                        setStep(15);
                      }, introButton);
                    }}
                    variant="primary"
                    className={`flex-next`}
                    disabled={!selectedFocusProgram?.length}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}

            {step === 15 && (
              <>
                <div className="max-width-576 route-fade-in">
                  <h1 className="page-sub-title text-center mt-0 mb-12">{`Any injuries or limitations to keep in mind?`}</h1>
                  <p className="text-light text-center mt-0 mb-32">
                    {' '}
                    {`Select an area to add more details.`}
                  </p>
                </div>

                <div className="max-width-768 route-fade-in">
                  <div className=" injurie-grid-container grid-lg-4 grid-md-3 grid-2 route-fade-in mb-32">
                    {traniningLimitation?.map((injurie) => (
                      <Button
                        key={injurie?.value}
                        onClick={() => injuriesHandler(injurie?.value)}
                        variant="primary"
                        className={`${isSelectedInjuries(injurie?.value) ? `active` : ``}`}
                      >
                        {injurie?.image && <img src={injurie?.image} alt={injurie?.name} />}
                        <p className="mt-0">{injurie?.name}</p>
                      </Button>
                    ))}
                  </div>

                  <div className="requirement-wrapper route-fade-in">
                    <label className="fs-18">Requirements (optional)</label>
                    <textarea
                      placeholder="No jumping exercises,..."
                      rows={4}
                      className="fs-18"
                      value={requirement}
                      onChange={(e) => requirementHandler(e.target.value)}
                    />
                  </div>
                </div>

                <br />
                <div className="max-width-next route-fade-in">
                  <Button
                    onClick={() => {
                      setTimeout(() => {
                        setStep(16);
                      }, introButton);
                    }}
                    variant="primary"
                    className={`flex-next`}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}

            {step === 16 && !isGenerating && (
              <>
                <div className="max-width-576 mb-14">
                  <h1 className="page-sub-title text-center mt-0">{`Let’s create the program based on your responses`}</h1>
                </div>
                <div
                  className={`max-width-576 p-3 border-radius-16 center-text flex-center-column`}
                >
                  {isSummarizing && (
                    <>
                      <style>
                        {`@keyframes gradientMove {
                          0% { background-position: 0% 50%; }
                          100% { background-position: -200% 50%; }
                        }`}
                      </style>

                      <span
                        className={`loading-title`}
                        style={{
                          background: 'linear-gradient(90deg, #B68031, #FFFFFF, #B68031)',
                          backgroundSize: '200% 100%',
                          animation: 'gradientMove 3s linear infinite',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textAlign: 'center',
                        }}
                      >
                        Generating instructions...
                      </span>
                    </>
                  )}
                  {!isSummarizing && (
                    <p
                      className="text-start mt-8"
                      style={{ minHeight: '80px', whiteSpace: 'pre-wrap' }}
                    >
                      {renderFormattedText(
                        summaryData?.response
                          ? summaryData.response
                          : `I will prepare a ${programDuration}-week program for ${firstName} at ${fitnessLevels?.[fitnessLevel - 1]?.name || 'intermediate'} level, focusing on ${selectedFocusProgram.join(', ')}, taking into account any limitations.`,
                      )}
                    </p>
                  )}
                  {!isSummarizing && (
                    <p style={{ marginBottom: 8, marginTop: 32 }} className="text-start">
                      Do you want to add anything?
                    </p>
                  )}
                  {!isSummarizing && (
                    <div className="prompt-container">
                      {prompts?.map((prompt, index) => (
                        <p
                          className={`prompt prompt-btn ${selectedSummaryPrompts.includes(prompt) ? 'active' : ''} ${
                            isSummarizing ? 'disabled' : ''
                          }`}
                          aria-disabled={isSummarizing}
                          key={index}
                          onClick={() => {
                            if (!isSummarizing) {
                              handleSummaryPrompt(prompt);
                            }
                          }}
                        >
                          {prompt}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="max-width-576" style={{ marginTop: 24 }}>
                  <Button
                    onClick={() => {
                      if (containerRef.current) {
                        gsap.to(window, {
                          duration: 0.6,
                          scrollTo: containerRef.current,
                          ease: 'power2.inOut',
                        });
                      }

                      // Fire analytics snapshot – do not await, must not block the generate flow.
                      // No sessionId on this first call; the backend generates one and returns it.
                      // We persist the returned ID so the Get Early Access call can reuse it.
                      trackUserBehavior({
                        email: '',
                        data: screenTimeTracker.getReport(),
                      })
                        .then((res) => {
                          screenTimeTracker.setSessionId(res.sessionId);
                        })
                        .catch(() => {
                          // Silently ignore – analytics are non-critical
                        });

                      const payload = buildWorkoutProgramPayload(
                        selectedSummaryPrompts.length > 0 ? selectedSummaryPrompts.join('. ') : '',
                      );
                      generateWorkout(payload, {
                        onSuccess: (data) => {
                          if (!data?.program) {
                            setGenerateErrorVisible(true);
                            return;
                          }

                          navigate('/trainer/generated/ai', {
                            state: { program: data?.program, payload },
                          });
                        },
                        onError: () => {
                          setGenerateErrorVisible(true);
                        },
                      });
                    }}
                    variant="primary"
                    disabled={isGenerating || !canProceedFromSummary}
                    className={`bg-gradient-color w-100 justify-content-center`}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.6861 8.78076L9.25273 11.0808C9.13051 11.2697 8.96118 11.3474 8.74473 11.3141C8.52829 11.2808 8.39207 11.153 8.33607 10.9308L7.8694 9.0641L3.3194 13.6141C3.19718 13.7363 3.04451 13.8003 2.8614 13.8061C2.67829 13.8119 2.51985 13.7479 2.38607 13.6141C2.26385 13.4919 2.20273 13.3363 2.20273 13.1474C2.20273 12.9585 2.26385 12.803 2.38607 12.6808L6.93607 8.1141L5.0694 7.64743C4.84718 7.59188 4.7194 7.45588 4.68607 7.23943C4.65273 7.02299 4.73051 6.85343 4.9194 6.73076L7.2194 5.3141L7.0194 2.59743C6.99718 2.37521 7.08607 2.2141 7.28607 2.1141C7.48607 2.0141 7.6694 2.03632 7.83607 2.18076L9.9194 3.93076L12.4361 2.9141C12.6472 2.82521 12.8305 2.85854 12.9861 3.0141C13.1416 3.16965 13.175 3.35299 13.0861 3.5641L12.0694 6.08076L13.8194 8.14743C13.9638 8.3141 13.9861 8.49743 13.8861 8.69743C13.7861 8.89743 13.625 8.98632 13.4027 8.9641L10.6861 8.78076ZM2.15273 4.3141C2.08607 4.24743 2.05273 4.16965 2.05273 4.08076C2.05273 3.99188 2.08607 3.9141 2.15273 3.84743L3.0194 2.98076C3.08607 2.9141 3.16385 2.88076 3.25273 2.88076C3.34162 2.88076 3.4194 2.9141 3.48607 2.98076L4.35273 3.84743C4.4194 3.9141 4.45273 3.99188 4.45273 4.08076C4.45273 4.16965 4.4194 4.24743 4.35273 4.3141L3.48607 5.18076C3.4194 5.24743 3.34162 5.28076 3.25273 5.28076C3.16385 5.28076 3.08607 5.24743 3.0194 5.18076L2.15273 4.3141ZM11.6861 13.8474L10.8194 12.9808C10.7527 12.9141 10.7194 12.8363 10.7194 12.7474C10.7194 12.6585 10.7527 12.5808 10.8194 12.5141L11.6861 11.6474C11.7527 11.5808 11.8305 11.5474 11.9194 11.5474C12.0083 11.5474 12.0861 11.5808 12.1527 11.6474L13.0194 12.5141C13.0861 12.5808 13.1194 12.6585 13.1194 12.7474C13.1194 12.8363 13.0861 12.9141 13.0194 12.9808L12.1527 13.8474C12.0861 13.9141 12.0083 13.9474 11.9194 13.9474C11.8305 13.9474 11.7527 13.9141 11.6861 13.8474Z"
                        fill="var(--dark-blue-light)"
                      />
                    </svg>
                    {isGenerating ? 'Generating...' : 'Generate with MiniPt AI'}
                  </Button>
                </div>
                <br />
                {/* <Button
                onClick={() => navigate('/trainer/generated/custom')}
                variant="outline"
                disabled={!canProceedFromSummary}
                className="continue-manually"
              >
                Continue Manually
              </Button> */}
              </>
            )}

            {step === 16 && isGenerating && (
              <div
                className="max-width-768 route-fade-in flex-center-column"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <style>
                  {`
                  @keyframes gradientMove {
                    0% { background-position: 0% 50%; }
                    100% { background-position: -200% 50%; }
                  }
                `}
                </style>
                <h1 className="page-sub-title text-center mb-32">
                  <span
                    style={{
                      background: 'linear-gradient(90deg, #B68031, #FFFFFF, #B68031)',
                      backgroundSize: '200% 100%',
                      animation: 'gradientMove 3s linear infinite',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Generating your
                    <br />
                    workout program
                  </span>
                </h1>
                <Button
                  variant="primary"
                  disabled
                  style={{
                    backgroundColor: '#FFA10012',
                    color: '#FFA100',
                    minWidth: '160px',
                    border: 'none',
                    opacity: 1,
                    borderRadius: '45px',
                  }}
                  className="justify-content-center"
                >
                  <LoadingDots />
                </Button>
              </div>
            )}
          </div>

          {step >= 5 && step <= 10 && (
            <AnimatedStep isVisible={step >= 5 && step <= 10} className="trainer-card-footer extra">
              <TrainingNavigationButton
                currentStep={step - 5}
                prevStep={() => {
                  setStep((prev) => prev - 1);
                }}
                nextStep={() => {
                  setStep((prev) => prev + 1);
                }}
                nextDisable={nextDisableStep}
                prevDisable={false}
                totalSteps={6}
              />
            </AnimatedStep>
          )}

          {step >= 12 && step <= 16 && (
            <AnimatedStep
              isVisible={step >= 12 && step <= 16}
              className="trainer-card-footer extra"
            >
              <TrainingNavigationButton
                currentStep={step - 12}
                prevStep={() => {
                  setStep((prev) => prev - 1);
                }}
                nextStep={() => {
                  setStep((prev) => prev + 1);
                }}
                nextDisable={nextDisableStep}
                prevDisable={false}
                totalSteps={4}
              />
            </AnimatedStep>
          )}
        </div>
      </div>
    </>
  );
};
